/**
 * ProjectLinks — Feb 2026 rewrite.
 *
 * 5 fully customizable workspace-link slots. Each slot lets the team pick a
 * label (e.g. "Drive", "Figma", "Notion — Retro", "Repo") AND URL. Falls
 * back to opinionated defaults (Drive · Figma · Slides · Link 4 · Link 5)
 * for empty slots so first-time editors see a helpful hint.
 *
 * Backwards-compat: old ProjectLink docs stored `kind` ("drive" | "figma" |
 * "slides" | "external1" | "external2"). We keep those kind values as the
 * stable slot identifier so upgrade doesn't stomp existing rows.
 */
import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { ExternalLink, Pencil, Folder, Figma, Presentation, Link as LinkIcon, Trash2 } from "lucide-react";
import { toast } from "sonner";

// 5 slots — kind is stable (for schema back-compat), label + URL are editable.
const SLOTS = [
  { kind: "drive", default_label: "Google Drive", Icon: Folder, color: "#0F9D58" },
  { kind: "figma", default_label: "Figma", Icon: Figma, color: "#A259FF" },
  { kind: "slides", default_label: "Slides", Icon: Presentation, color: "#F4B400" },
  { kind: "external1", default_label: "Link 4", Icon: LinkIcon, color: "#475569" },
  { kind: "external2", default_label: "Link 5", Icon: LinkIcon, color: "#475569" },
];

function findLink(links, kind) {
  return (links || []).find((l) => l.kind === kind) || null;
}

function normaliseUrl(u) {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  return `https://${u}`;
}

export default function ProjectLinks({ project, canEdit, canEditLink, onUpdated }) {
  // `canEdit` retained for backwards-compat; the actual gate for URL edits is
  // `canEditLink` (admin + admin-mode-on). Feb 2026 — labels are now editable
  // too (previously fixed).
  const adminCanEdit = canEditLink ?? canEdit;
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ label: "", url: "" });

  const links = project.links || [];

  const startEdit = (slot) => {
    const existing = findLink(links, slot.kind);
    setEditing(slot);
    setForm({
      label: existing?.label || slot.default_label,
      url: existing?.url || "",
    });
  };

  const save = async () => {
    if (!editing) return;
    const next = SLOTS.map((slot) => {
      const existing = findLink(links, slot.kind);
      if (slot.kind === editing.kind) {
        return {
          kind: slot.kind,
          label: (form.label || "").trim() || slot.default_label,
          url: normaliseUrl(form.url?.trim() || ""),
        };
      }
      return existing
        ? { ...existing }
        : { kind: slot.kind, label: slot.default_label, url: "" };
    }).filter((l) => l.url);  // Only persist slots that carry a URL.
    try {
      await api.patch(`/projects/${project.id}`, { links: next });
      toast.success("Link saved");
      setEditing(null);
      onUpdated && onUpdated();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const clearSlot = async (slot) => {
    if (!window.confirm(`Remove ${slot.default_label} link?`)) return;
    const next = (links || []).filter((l) => l.kind !== slot.kind);
    try {
      await api.patch(`/projects/${project.id}`, { links: next });
      toast.success("Link removed");
      setEditing(null);
      onUpdated && onUpdated();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="card-flat p-5 mb-6" data-testid="project-links">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="label-overline">Workspace links</div>
          <p className="text-xs text-slate-500 mt-0.5">Up to 5 external links · rename any of them to fit the project.</p>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {SLOTS.map((slot) => {
          const link = findLink(links, slot.kind);
          const Icon = slot.Icon;
          const url = link?.url || "";
          const label = link?.label || slot.default_label;
          return (
            <div key={slot.kind} className="relative group rounded-sm border border-slate-200 hover:border-slate-300 transition-colors" data-testid={`project-link-${slot.kind}`}>
              {url ? (
                <a href={url} target="_blank" rel="noreferrer noopener" className="flex items-center gap-2.5 p-3 min-h-[64px]" data-testid={`project-link-open-${slot.kind}`}>
                  <span className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0" style={{ background: `${slot.color}1A`, color: slot.color }}>
                    <Icon size={16} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs font-semibold text-slate-900 truncate" data-testid={`project-link-label-${slot.kind}`}>{label}</span>
                    <span className="block text-[10px] text-slate-400 truncate flex items-center gap-1"><ExternalLink size={9} />Open</span>
                  </span>
                </a>
              ) : (
                <button
                  type="button"
                  onClick={() => adminCanEdit && startEdit(slot)}
                  disabled={!adminCanEdit}
                  className={`flex items-center gap-2.5 p-3 min-h-[64px] w-full text-left ${adminCanEdit ? "hover:bg-slate-50 cursor-pointer" : "cursor-not-allowed opacity-60"}`}
                  data-testid={`project-link-add-${slot.kind}`}
                >
                  <span className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0 bg-slate-100 text-slate-400">
                    <Icon size={16} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs font-semibold text-slate-500 truncate">{label}</span>
                    <span className="block text-[10px] text-slate-400 truncate">{adminCanEdit ? "Add link" : "—"}</span>
                  </span>
                </button>
              )}
              {adminCanEdit && url && (
                <button
                  type="button"
                  onClick={() => startEdit(slot)}
                  className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 p-1 rounded-sm bg-white border border-slate-200 hover:bg-slate-50 transition-opacity"
                  title="Edit label / URL"
                  data-testid={`project-link-edit-${slot.kind}`}
                >
                  <Pencil size={10} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle className="font-display">{editing?.default_label}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="label-overline">Label</Label>
              <Input
                className="rounded-sm mt-1"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder={editing?.default_label}
                data-testid="project-link-label-input"
                maxLength={40}
              />
              <p className="text-[10px] text-slate-400 mt-1">Rename this slot (e.g. “Notion — Retro”, “GitHub”, “Loom”).</p>
            </div>
            <div>
              <Label className="label-overline">URL</Label>
              <Input
                className="rounded-sm mt-1"
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="https://…"
                data-testid="project-link-url"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            {editing && findLink(links, editing.kind)?.url && (
              <Button
                variant="outline"
                onClick={() => clearSlot(editing)}
                className="rounded-sm text-red-700 border-red-200 hover:bg-red-50"
                data-testid="project-link-clear"
              >
                <Trash2 size={13} className="mr-1" /> Remove
              </Button>
            )}
            <Button onClick={save} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="save-project-link">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
